/**
 * Header content shape — config-driven editorial chrome (top strip,
 * search overlay, mobile drawer, theme tokens). Mirror of footerContent
 * so dedicated layouts (AtelierLayout — and over time LuxeLayout /
 * NeonLayout / KineticLayout) can read tenant overrides from
 * storefrontConfig instead of hardcoding colour + font tokens.
 */

export interface HeaderTopStrip {
  /** Show the editorial hairline strip above the main header row. Default true. */
  enabled?: boolean;
  /** Left-aligned label, e.g. "MAISON · EST. MMXXVI". */
  left?: string;
  /** Center label, typically the in-view chapter / collection. */
  center?: string;
  /** Right-aligned label, e.g. region or store hours. */
  right?: string;
}

/**
 * Theme tokens consumed by full-template header layouts. Each layout
 * supplies its own defaults — the merchant can override any token
 * via `storefrontConfig.headerContent.theme.<key>` and the rest fall
 * through to the layout's preset.
 */
export interface HeaderTheme {
  /** Header background colour. */
  bgColor?: string;
  /** Background colour applied when the page has scrolled. */
  bgScrolledColor?: string;
  /** Primary text colour for nav links + utility icons. */
  textColor?: string;
  /** Muted text colour (top strip, drawer footnote, etc.). */
  mutedTextColor?: string;
  /** Brand accent (e.g. gold / cyan / lime) — used for hover, badges, hairline. */
  accentColor?: string;
  /** Lighter accent tint — used for active link colour. */
  accentLightColor?: string;
  /** Hairline / divider colour (default-derived from accent). */
  borderColor?: string;
  /** Stronger gold/accent border for scroll state. */
  borderActiveColor?: string;

  /** Wordmark / brand-name typography. */
  brandFontFamily?: string;
  brandFontStyle?: 'normal' | 'italic' | string;
  brandTextTransform?: 'none' | 'uppercase' | 'lowercase' | string;
  brandLetterSpacing?: string;
  brandFontWeight?: string | number;

  /** Nav link typography. */
  navFontFamily?: string;
  navTextTransform?: 'none' | 'uppercase' | 'lowercase' | string;
  navLetterSpacing?: string;
  navFontWeight?: string | number;
  navFontSize?: string;

  /** Show the search button in the header. Default depends on layout. */
  showSearch?: boolean;
  /** Show the cart icon. Default true. */
  showCart?: boolean;

  /** Scrolling ticker strip above the header (kinetic-style). Layouts
   *  that don't opt in ignore this. */
  tickerEnabled?: boolean;
  /** Items to scroll. Each becomes one segment. */
  tickerItems?: string[];
  /** Animation duration in seconds (one full loop). */
  tickerSpeedSeconds?: number;
}

/** A single nav link rendered in the header. */
export interface HeaderNavItem {
  label: string;
  url: string;
}

/** Primary header call-to-action (e.g. "Start free trial"). */
export interface HeaderCta {
  /** Show the CTA in the header. Default true when label is set. */
  enabled?: boolean;
  /** Visible label. */
  label?: string;
  /** Where the CTA points. */
  url?: string;
}

export interface HeaderContent {
  /** Editorial hairline strip above the brand wordmark. */
  topStrip?: HeaderTopStrip;
  /** Nav links rendered in the desktop header. Each Layout supplies
   *  industry-appropriate defaults; merchants override via the builder. */
  navItems?: HeaderNavItem[];
  /** Primary CTA pill rendered on the right side of the header. */
  primaryCta?: HeaderCta;
  /** Optional secondary CTA (e.g. "Sign in" on SaaS templates). */
  secondaryCta?: HeaderCta;
  /** Footer text inside the mobile drawer (e.g. phone / "By appointment"). */
  mobileFootnote?: string;
  /** Search overlay placeholder. */
  searchPlaceholder?: string;
  /** Search overlay hint copy below the input. */
  searchHint?: string;
  /** Theme tokens consumed by full-template layouts. Defaults baked
   *  into each layout; any token here overrides the matching default. */
  theme?: HeaderTheme;
}

export interface HeaderContentDefaults extends HeaderContent {
  storeName?: string;
}

const sanitizeString = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : fallback;
};

const passThroughString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : undefined;
};

const resolveTheme = (raw: any, defaults: HeaderTheme): HeaderTheme => {
  const r = raw && typeof raw === 'object' ? raw : {};
  const merged: HeaderTheme = {
    bgColor: passThroughString(r.bgColor) ?? defaults.bgColor,
    bgScrolledColor: passThroughString(r.bgScrolledColor) ?? defaults.bgScrolledColor,
    textColor: passThroughString(r.textColor) ?? defaults.textColor,
    mutedTextColor: passThroughString(r.mutedTextColor) ?? defaults.mutedTextColor,
    accentColor: passThroughString(r.accentColor) ?? defaults.accentColor,
    accentLightColor: passThroughString(r.accentLightColor) ?? defaults.accentLightColor,
    borderColor: passThroughString(r.borderColor) ?? defaults.borderColor,
    borderActiveColor: passThroughString(r.borderActiveColor) ?? defaults.borderActiveColor,
    brandFontFamily: passThroughString(r.brandFontFamily) ?? defaults.brandFontFamily,
    brandFontStyle: passThroughString(r.brandFontStyle) ?? defaults.brandFontStyle,
    brandTextTransform: passThroughString(r.brandTextTransform) ?? defaults.brandTextTransform,
    brandLetterSpacing: passThroughString(r.brandLetterSpacing) ?? defaults.brandLetterSpacing,
    brandFontWeight:
      typeof r.brandFontWeight === 'string' || typeof r.brandFontWeight === 'number'
        ? r.brandFontWeight
        : defaults.brandFontWeight,
    navFontFamily: passThroughString(r.navFontFamily) ?? defaults.navFontFamily,
    navTextTransform: passThroughString(r.navTextTransform) ?? defaults.navTextTransform,
    navLetterSpacing: passThroughString(r.navLetterSpacing) ?? defaults.navLetterSpacing,
    navFontWeight:
      typeof r.navFontWeight === 'string' || typeof r.navFontWeight === 'number'
        ? r.navFontWeight
        : defaults.navFontWeight,
    navFontSize: passThroughString(r.navFontSize) ?? defaults.navFontSize,
    showSearch:
      typeof r.showSearch === 'boolean' ? r.showSearch : defaults.showSearch,
    showCart:
      typeof r.showCart === 'boolean' ? r.showCart : defaults.showCart,
    tickerEnabled:
      typeof r.tickerEnabled === 'boolean' ? r.tickerEnabled : defaults.tickerEnabled,
    tickerItems: Array.isArray(r.tickerItems)
      ? (r.tickerItems as any[]).filter((s) => typeof s === 'string' && s.trim().length > 0)
      : defaults.tickerItems,
    tickerSpeedSeconds:
      typeof r.tickerSpeedSeconds === 'number' && Number.isFinite(r.tickerSpeedSeconds)
        ? r.tickerSpeedSeconds
        : defaults.tickerSpeedSeconds,
  };
  return merged;
};

export const resolveHeaderContent = (
  config: any,
  defaults: HeaderContent,
): HeaderContent => {
  const raw: any = config && typeof config === 'object' ? config : {};
  const rawStrip = raw?.topStrip || {};
  const defaultStrip = defaults.topStrip || {};

  const topStrip: HeaderTopStrip = {
    enabled:
      typeof rawStrip.enabled === 'boolean'
        ? rawStrip.enabled
        : defaultStrip.enabled ?? true,
    left: sanitizeString(rawStrip.left, defaultStrip.left || ''),
    center: sanitizeString(rawStrip.center, defaultStrip.center || ''),
    right: sanitizeString(rawStrip.right, defaultStrip.right || ''),
  };

  // Nav items — config overrides defaults entirely (so merchants don't get
  // forced-prepended links). Each item normalized to { label, url }.
  const sanitizeNav = (arr: any[]): HeaderNavItem[] =>
    arr
      .map((n) => ({
        label: typeof n?.label === 'string' ? n.label.trim() : '',
        url: typeof n?.url === 'string' ? n.url.trim() : (typeof n?.href === 'string' ? n.href.trim() : ''),
      }))
      .filter((n) => n.label.length > 0 && n.url.length > 0);
  const navItems: HeaderNavItem[] = Array.isArray(raw?.navItems)
    ? sanitizeNav(raw.navItems)
    : (defaults.navItems || []);

  const sanitizeCta = (
    r: any,
    fallback: HeaderCta | undefined,
  ): HeaderCta | undefined => {
    const rawObj: any = r && typeof r === 'object' ? r : {};
    const fb = fallback || {};
    const label = passThroughString(rawObj.label) ?? fb.label;
    const url = passThroughString(rawObj.url) ?? passThroughString(rawObj.href) ?? fb.url;
    if (!label) return fallback;
    const enabled =
      typeof rawObj.enabled === 'boolean'
        ? rawObj.enabled
        : (typeof fb.enabled === 'boolean' ? fb.enabled : true);
    return { enabled, label, url };
  };

  return {
    topStrip,
    navItems,
    primaryCta: sanitizeCta(raw?.primaryCta, defaults.primaryCta),
    secondaryCta: sanitizeCta(raw?.secondaryCta, defaults.secondaryCta),
    mobileFootnote: sanitizeString(raw?.mobileFootnote, defaults.mobileFootnote || ''),
    searchPlaceholder: sanitizeString(
      raw?.searchPlaceholder,
      defaults.searchPlaceholder || 'Search...',
    ),
    searchHint: sanitizeString(raw?.searchHint, defaults.searchHint || ''),
    theme: resolveTheme(raw?.theme, defaults.theme || {}),
  };
};

export const pickHeaderContent = (storefrontConfig: any): any => {
  if (!storefrontConfig || typeof storefrontConfig !== 'object') return null;
  return (
    storefrontConfig.headerContent ||
    storefrontConfig.storefrontConfig?.headerContent ||
    null
  );
};
